import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { MainAction } from "./MainAction";
import { ApiError, fetchBirthday, saveBirthday } from "@/lib/api";
import { haptic } from "@/lib/telegram";

const MONTHS = { ru: ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"], uz: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr"], en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] };

/** Profile row + sheet: the customer tells us their birthday (day + month; year optional). */
/** `variant="row"` renders it as a row inside a profile group (same sheet and logic). */
export function BirthdayRow({ variant = "card", icon }: { variant?: "card" | "row"; icon?: React.ReactNode } = {}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState<number | "">("");
  const [month, setMonth] = useState<number | "">("");
  const [year, setYear] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const names = MONTHS[locale as keyof typeof MONTHS] ?? MONTHS.ru;

  useEffect(() => {
    fetchBirthday()
      .then((b) => {
        if (b.day && b.month) {
          setDay(b.day);
          setMonth(b.month);
          setYear(b.year ? String(b.year) : "");
          setSaved(`${b.day} ${names[b.month - 1]}`);
        }
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setError(null);
    try {
      await saveBirthday({ day: day || null, month: month || null, year: year ? Number(year) : null });
      haptic("success");
      setSaved(day && month ? `${day} ${names[Number(month) - 1]}` : null);
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    }
  };

  return (
    <>
      {variant === "row" ? (
        <button className="pf-row" onClick={() => setOpen(true)}>
          <span className="pf-ic">{icon}</span>
          <span className="pf-row__label">{t("birthday.row")}</span>
          <span className="pf-row__val">{saved ?? t("profile.set")}</span>
          <span className="pf-row__chev" aria-hidden="true">›</span>
        </button>
      ) : (
      <button className="card biz-row" onClick={() => setOpen(true)}>
          <span>
            <b>🎂 {t("birthday.row")}</b>
            <small>{saved ?? t("birthday.rowHint")}</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>
      )}
      {open && (
        <div className="sheet-overlay" onClick={() => setOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{t("birthday.title")}</h2>
            <p className="sheet__text">{t("birthday.text")}</p>
            <div className="lead-grid">
              <div className="field">
                <label>{t("birthday.day")}</label>
                <select value={day} onChange={(e) => setDay(e.target.value ? Number(e.target.value) : "")}>
                  <option value="">—</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="field">
                <label>{t("birthday.month")}</label>
                <select value={month} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : "")}>
                  <option value="">—</option>
                  {names.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label>{t("birthday.year")}</label>
              <input inputMode="numeric" maxLength={4} value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder={t("birthday.yearHint")} />
            </div>
            {error && <div className="form-error-banner">{error}</div>}
            <MainAction text={t("birthday.save")} onClick={save} />
            {saved && (
              <button className="btn btn--danger-text" style={{ width: "100%", marginTop: 8 }} onClick={() => { setDay(""); setMonth(""); setYear(""); }}>
                {t("birthday.clear")}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
